/**
 * Ekvalizer kot čista logika (P2 §6.3, T1 §4).
 *
 * Tu ni ne `AudioContext` ne DOM: pasovi, prednastavitve in — najpomembnejše —
 * **odločitev, ali je Web Audio graf sploh potreben**. Ta odločitev je razlog,
 * zakaj datoteka obstaja: iz nje sledi, ali tok teče direktno ali prek
 * Workerja, in ta pot mora biti testabilna brez brskalnika.
 *
 * ⛔ **Ojačitev ni status** (CLAUDE.md, pravilo 3): 0 dB je nevtralna, boost in
 * cut nista `--success`/`--danger`. Polnilo je `--nw-eq-fill` v obe smeri.
 */

import type { EqPreset } from './settings';

/**
 * Deset pasov, oktavno razmaknjenih (P2 §6.3).
 *
 * Razmik je oktava (×2), ker je sluh logaritmičen — linearna razdelitev bi
 * osem od desetih drsnikov postavila v višine, kjer se skoraj nič ne sliši.
 */
export const EQ_BANDS: readonly number[] = [31, 62, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];

export const EQ_MIN_DB = -12;
export const EQ_MAX_DB = 12;

/**
 * Prednastavitve (P2 §6.3, osem imenovanih + `custom`).
 *
 * Vrednosti niso prepisane iz nobene specifikacije — ta zanje ne obstaja.
 * Načelo je: **nobena krivulja ne dvigne vsega**, ker bi to bila glasnost in
 * ne ekvalizacija, in nobena ne preseže ±8 dB, ker tok pri višjih ojačitvah
 * zaklipa, preden se sliši kot izboljšava.
 *
 * `custom` tu namenoma nima vnosa: nastane iz uporabnikovih drsnikov in ni
 * krivulja, ki bi jo lahko obnovili — `matchPreset()` jo ugotovi, ne prebere.
 */
export const EQ_PRESETS: Record<Exclude<EqPreset, 'custom'>, readonly number[]> = {
  flat: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  rock: [5, 4, 3, 1, -1, -1, 1, 3, 4, 4],
  pop: [-1, 0, 2, 4, 4, 3, 1, -1, -1, -2],
  jazz: [3, 2, 1, 2, 0, -1, 0, 1, 2, 3],
  classical: [4, 3, 2, 0, -1, -1, 0, 2, 3, 4],
  bass: [8, 7, 5, 3, 1, 0, 0, 0, 0, 0],
  treble: [0, 0, 0, 0, 0, 1, 3, 5, 7, 8],
  vocal: [-3, -2, -1, 2, 4, 4, 3, 1, 0, -1],
};

/** Vrstni red v izbirniku; `custom` je zadnja in je nikoli ne izbere uporabnik. */
export const EQ_PRESET_ORDER: readonly Exclude<EqPreset, 'custom'>[] = [
  'flat',
  'rock',
  'pop',
  'jazz',
  'classical',
  'bass',
  'treble',
  'vocal',
];

/** Cela decibela v obsegu −12..+12; vse drugo se odreže. */
export function clampGain(db: number): number {
  if (!Number.isFinite(db)) return 0;
  return Math.min(EQ_MAX_DB, Math.max(EQ_MIN_DB, Math.round(db)));
}

/**
 * Polje z natanko `EQ_BANDS.length` veljavnimi ojačitvami.
 *
 * Obramba pred zapisom iz shrambe ali uvožene datoteke: `parseSettings()` že
 * zavrne napačno dolžino, ne pa vrednosti izven obsega. Filter z ojačitvijo
 * +40 dB ni EQ, ampak popačenje, in uporabnik ne bi vedel, od kod prihaja.
 */
export function normaliseGains(gains: readonly number[]): number[] {
  return EQ_BANDS.map((_, index) => clampGain(gains[index] ?? 0));
}

/** Ali je krivulja ravna — torej ali EQ zvoka sploh spreminja. */
export function isFlat(gains: readonly number[]): boolean {
  return gains.every((gain) => gain === 0);
}

/**
 * Katera prednastavitev ustreza tem ojačitvam; `custom`, kadar nobena.
 *
 * Shranjeno `settings.eqPreset` ni vir resnice — vir so ojačitve. Uporabnik
 * lahko izbere »rock« in nato premakne en drsnik; izbirnik mora takrat
 * pokazati »po meri«, sicer trdi nekaj, česar ne sliši.
 */
export function matchPreset(gains: readonly number[]): EqPreset {
  const normalised = normaliseGains(gains);
  for (const preset of EQ_PRESET_ORDER) {
    const curve = EQ_PRESETS[preset];
    if (normalised.every((gain, index) => gain === curve[index])) return preset;
  }
  return 'custom';
}

/** Ojačitve prednastavitve; za `custom` vrne trenutne, ker svoje krivulje nima. */
export function presetGains(preset: EqPreset, current: readonly number[]): number[] {
  if (preset === 'custom') return normaliseGains(current);
  return [...EQ_PRESETS[preset]];
}

/**
 * 🔴 **Osrednje vedenjsko pravilo aplikacije** (T1 §4, CLAUDE.md, pravilo 2).
 *
 * Web Audio graf sme nastati **samo**, kadar ga uporabnik dejansko rabi:
 * vsaj en pas ≠ 0 dB ali vklopljen vizualizator. Iz tega sledi pot toka —
 * graf zahteva CORS, CORS zahteva Worker.
 *
 * Vklopljen EQ z ravno krivuljo torej **ne** šteje. To ni varčevanje z
 * zahtevki: `createMediaElementSource()` na ne-CORS viru odda ničle in zvok
 * utihne brez napake, obratne poti pa ni — klic se ne da razveljaviti.
 */
export function needsAudioGraph(settings: {
  eqEnabled: boolean;
  eqGains: readonly number[];
  visualizerEnabled: boolean;
}): boolean {
  if (settings.visualizerEnabled) return true;
  return settings.eqEnabled && !isFlat(normaliseGains(settings.eqGains));
}

/**
 * Ojačitve, ki gredo v graf; `null`, kadar EQ ni dejaven.
 *
 * `null` in »samo ničle« sta za adapter ista stvar (filtri pri 0 dB ne
 * spreminjajo ničesar), a klicno mesto ju loči: `null` pomeni »uporabnik EQ ne
 * uporablja«, ničle pomenijo »uporablja ga in ga je poravnal«.
 */
export function activeGains(settings: {
  eqEnabled: boolean;
  eqGains: readonly number[];
}): number[] | null {
  return settings.eqEnabled ? normaliseGains(settings.eqGains) : null;
}
